import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Fade,
  Slide,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  Schedule as ScheduleIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Analytics as AnalyticsIcon,
  Speed as SpeedIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  ScatterChart,
  Scatter,
} from 'recharts';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { de } from 'date-fns/locale';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Mock data for demonstration
  const conversationData = [
    { date: '2024-01-09', conversations: 45, resolved: 38, satisfaction: 4.2 },
    { date: '2024-01-10', conversations: 52, resolved: 48, satisfaction: 4.5 },
    { date: '2024-01-11', conversations: 38, resolved: 35, satisfaction: 4.1 },
    { date: '2024-01-12', conversations: 61, resolved: 55, satisfaction: 4.3 },
    { date: '2024-01-13', conversations: 48, resolved: 42, satisfaction: 4.0 },
    { date: '2024-01-14', conversations: 25, resolved: 22, satisfaction: 4.4 },
    { date: '2024-01-15', conversations: 18, resolved: 16, satisfaction: 4.6 },
  ];

  const userActivityData = [
    { hour: '00:00', users: 2, conversations: 1 },
    { hour: '04:00', users: 1, conversations: 0 },
    { hour: '08:00', users: 15, conversations: 8 },
    { hour: '12:00', users: 28, conversations: 18 },
    { hour: '16:00', users: 35, conversations: 25 },
    { hour: '20:00', users: 22, conversations: 12 },
    { hour: '24:00', users: 8, conversations: 3 },
  ];

  const responseTimeData = [
    { name: 'Schnell', value: 65, color: '#4CAF50' },
    { name: 'Mittel', value: 25, color: '#FF9800' },
    { name: 'Langsam', value: 10, color: '#F44336' },
  ];

  const topQuestions = [
    { question: 'Welche Fahrradgröße brauche ich?', count: 45, satisfaction: 4.5 },
    { question: 'Wie oft sollte ich mein Fahrrad warten?', count: 38, satisfaction: 4.2 },
    { question: 'Welches E-Bike ist für mich geeignet?', count: 32, satisfaction: 4.3 },
    { question: 'Wie repariere ich eine kaputte Kette?', count: 28, satisfaction: 4.1 },
    { question: 'Welche Sicherheitsausrüstung brauche ich?', count: 25, satisfaction: 4.4 },
  ];

  const botPerformanceData = [
    { name: 'Korrekte Antworten', value: 85, color: '#4CAF50' },
    { name: 'Teilweise korrekt', value: 10, color: '#FF9800' },
    { name: 'Falsche Antworten', value: 5, color: '#F44336' },
  ];

  const recentConversations = [
    {
      id: 1,
      user: 'Max Mustermann',
      question: 'Welche Fahrradgröße brauche ich für 1,80m?',
      response: 'Für Ihre Körpergröße von 1,80m empfehle ich ein Fahrrad mit einer Rahmengröße von 56-58cm...',
      satisfaction: 5,
      timestamp: '2024-01-15T14:30:00Z',
    },
    {
      id: 2,
      user: 'Anna Schmidt',
      question: 'Wie oft sollte ich die Kette ölen?',
      response: 'Die Fahrradkette sollte alle 200-300 km oder bei sichtbarer Verschmutzung geölt werden...',
      satisfaction: 4,
      timestamp: '2024-01-15T13:45:00Z',
    },
    {
      id: 3,
      user: 'Tom Weber',
      question: 'Welches E-Bike ist für den Stadtverkehr geeignet?',
      response: 'Für den Stadtverkehr empfehle ich ein E-Bike mit einer Reichweite von 50-80km...',
      satisfaction: 5,
      timestamp: '2024-01-15T12:20:00Z',
    },
  ];

  const stats = {
    totalConversations: 1247,
    todayConversations: 156,
    averageSatisfaction: 4.3,
    averageResponseTime: 1.2,
    resolutionRate: 89.5,
    activeUsers: 89,
  };

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    // Simulate export functionality
    console.log('Exporting analytics data...');
  };

  const StatCard = ({ title, value, icon, color, trend, subtitle }) => (
    <Fade in timeout={600}>
      <Card
        sx={{
          height: '100%',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          border: '1px solid #e9ecef',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: color + '20',
                color: color,
              }}
            >
              {icon}
            </Box>
            {trend && (
              <Chip
                label={trend}
                size="small"
                color="success"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
          
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#000000' }}>
            {value}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {title}
          </Typography>
          
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Fade>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Analytics Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Detaillierte Einblicke in die Performance Ihres KiChatbots
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Zeitraum</InputLabel>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                label="Zeitraum"
              >
                <MenuItem value="1d">Heute</MenuItem>
                <MenuItem value="7d">7 Tage</MenuItem>
                <MenuItem value="30d">30 Tage</MenuItem>
                <MenuItem value="90d">90 Tage</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadAnalytics}
              disabled={loading}
              sx={{
                borderColor: '#FFD700',
                color: '#FFD700',
                fontWeight: 600,
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#F57F17',
                  backgroundColor: 'rgba(255, 215, 0, 0.1)',
                },
              }}
            >
              Aktualisieren
            </Button>
            
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{
                background: 'linear-gradient(135deg, #FFD700 0%, #F57F17 100%)',
                color: '#000000',
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
              }}
            >
              Exportieren
            </Button>
          </Box>
        </Box>
        
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        <Typography variant="caption" color="text.secondary">
          Letzte Aktualisierung: {lastUpdated.toLocaleTimeString()}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Gespräche heute"
            value={stats.todayConversations}
            icon={<ChatIcon sx={{ fontSize: 28 }} />}
            color="#2196F3"
            trend="+23%"
            subtitle="vs. gestern"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Durchschnittliche Zufriedenheit"
            value={stats.averageSatisfaction}
            icon={<ThumbUpIcon sx={{ fontSize: 28 }} />}
            color="#4CAF50"
            trend="+0.2"
            subtitle="von 5.0"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Antwortzeit"
            value={`${stats.averageResponseTime}s`}
            icon={<SpeedIcon sx={{ fontSize: 28 }} />}
            color="#FF9800"
            trend="-0.3s"
            subtitle="Durchschnitt"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Lösungsrate"
            value={`${stats.resolutionRate}%`}
            icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
            color="#9C27B0"
            trend="+5%"
            subtitle="Diese Woche"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <StatCard
            title="Aktive Benutzer"
            value={stats.activeUsers}
            icon={<PeopleIcon sx={{ fontSize: 28 }} />}
            color="#F44336"
            trend="+12%"
            subtitle="Heute"
          />
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Conversation Trends */}
        <Grid item xs={12} md={8}>
          <Slide in timeout={800}>
            <Card sx={{ height: 400, borderRadius: 3 }}>
              <CardContent sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Gespräche und Zufriedenheit
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={conversationData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" stroke="#666" />
                    <YAxis yAxisId="left" stroke="#666" />
                    <YAxis yAxisId="right" orientation="right" stroke="#666" />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      }}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="conversations"
                      stackId="1"
                      stroke="#FFD700"
                      fill="url(#colorConversations)"
                      strokeWidth={2}
                    />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="resolved"
                      stackId="1"
                      stroke="#4CAF50"
                      fill="url(#colorResolved)"
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="satisfaction"
                      stroke="#9C27B0"
                      strokeWidth={3}
                      dot={{ fill: '#9C27B0', strokeWidth: 2, r: 4 }}
                    />
                    <defs>
                      <linearGradient id="colorConversations" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FFD700" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#FFD700" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorResolved" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#4CAF50" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Bot Performance */}
        <Grid item xs={12} md={4}>
          <Slide in timeout={1000}>
            <Card sx={{ height: 400, borderRadius: 3 }}>
              <CardContent sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Bot Performance
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={botPerformanceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {botPerformanceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 2 }}>
                  {botPerformanceData.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: item.color,
                          borderRadius: '50%',
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.value}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* User Activity */}
        <Grid item xs={12} md={6}>
          <Slide in timeout={1200}>
            <Card sx={{ height: 350, borderRadius: 3 }}>
              <CardContent sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Benutzeraktivität (24h)
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={userActivityData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="hour" stroke="#666" />
                    <YAxis stroke="#666" />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      }}
                    />
                    <Legend />
                    <Bar dataKey="users" fill="#FFD700" name="Benutzer" />
                    <Bar dataKey="conversations" fill="#4CAF50" name="Gespräche" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Response Time Distribution */}
        <Grid item xs={12} md={6}>
          <Slide in timeout={1400}>
            <Card sx={{ height: 350, borderRadius: 3 }}>
              <CardContent sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Antwortzeit-Verteilung
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={responseTimeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {responseTimeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ mt: 2 }}>
                  {responseTimeData.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: item.color,
                          borderRadius: '50%',
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.value}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>

      {/* Bottom Row */}
      <Grid container spacing={3}>
        {/* Top Questions */}
        <Grid item xs={12} md={6}>
          <Slide in timeout={1600}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Häufigste Fragen
                </Typography>
                <List>
                  {topQuestions.map((item, index) => (
                    <Fade key={index} in timeout={300 + index * 100}>
                      <Paper
                        elevation={1}
                        sx={{
                          mb: 2,
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid #e0e0e0',
                        }}
                      >
                        <ListItem sx={{ p: 0 }}>
                          <ListItemIcon>
                            <Chip
                              label={`#${index + 1}`}
                              size="small"
                              sx={{
                                backgroundColor: '#FFD700',
                                color: '#000000',
                                fontWeight: 600,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                                {item.question}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {item.count} Mal gefragt
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <ThumbUpIcon sx={{ fontSize: 16, color: '#4CAF50', mr: 0.5 }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {item.satisfaction}/5
                                  </Typography>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                      </Paper>
                    </Fade>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Recent Conversations */}
        <Grid item xs={12} md={6}>
          <Slide in timeout={1800}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Letzte Gespräche
                </Typography>
                <List>
                  {recentConversations.map((conversation, index) => (
                    <Fade key={conversation.id} in timeout={300 + index * 100}>
                      <Paper
                        elevation={1}
                        sx={{
                          mb: 2,
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid #e0e0e0',
                        }}
                      >
                        <ListItem sx={{ p: 0, alignItems: 'flex-start' }}>
                          <ListItemIcon sx={{ mt: 1 }}>
                            <ChatIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                  {conversation.user}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  {conversation.question}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {conversation.response.length > 100 
                                    ? `${conversation.response.substring(0, 100)}...`
                                    : conversation.response
                                  }
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {format(new Date(conversation.timestamp), 'dd.MM.yyyy HH:mm', { locale: de })}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <ThumbUpIcon sx={{ fontSize: 16, color: '#4CAF50', mr: 0.5 }} />
                                    <Typography variant="caption" color="text.secondary">
                                      {conversation.satisfaction}/5
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                      </Paper>
                    </Fade>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;
